import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useCreateSupportCase } from '../../data/support/useSupportCustomer';
import { SupportCasePriority } from '../../types/support.types';

interface CreateCaseDialogProps {
  open: boolean;
  onClose: () => void;
}

const PRIORITY_OPTIONS: SupportCasePriority[] = [
  'low',
  'medium',
  'high',
  'critical',
];

const CreateCaseDialog: React.FC<CreateCaseDialogProps> = ({
  open,
  onClose,
}) => {
  const [subject, setSubject] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [priority, setPriority] = React.useState<SupportCasePriority>('medium');
  const [error, setError] = React.useState<string | null>(null);
  const createCase = useCreateSupportCase();

  const handleSubmit = () => {
    setError(null);
    createCase.mutate(
      { subject, description, priority },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
        onError: (err: any) => {
          const detail = err?.response?.data?.detail || 'Failed to create case';
          setError(detail);
        },
      },
    );
  };

  const resetForm = () => {
    setSubject('');
    setDescription('');
    setPriority('medium');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Support Case</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          label="Subject"
          fullWidth
          size="small"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          label="Description"
          fullWidth
          size="small"
          required
          multiline
          minRows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Priority"
          fullWidth
          size="small"
          select
          value={priority}
          onChange={(e) => setPriority(e.target.value as SupportCasePriority)}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!subject || !description || createCase.isPending}
        >
          {createCase.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCaseDialog;
